import { Col, Layout, Row, Space, Typography, message } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ArrowLeftIconComponent from "../../../components/ArrowLeftIconComponent";
import DatePickerComponent from "../../../components/DatePickerComponent";
import EditProfileSwitchComponent from "../../../components/EditProfileSwitchComponent";
import InputComponent from "../../../components/InputComponent";
import StickyButtonComponent from "../../../components/StickyButtonComponent";
import TextAreaComponent from "../../../components/TextAreaComponent";
import { REQUEST_LOADING_STATES } from "../../../helpers/constant";
import {
  IEditCreatorChatDefaultPageRequest,
  IEditCreatorPersonalInfosRequest,
  defaultCreatorEditChatDefaultPageInfosRequest,
  defaultCreatorEditPersonalInfosRequest,
} from "../../../helpers/requests";
import useFetchUserInfos from "../../../hooks/userHooks/useFetchUserInfos";
import useFetchUserSettings from "../../../hooks/userHooks/useFetchUserSettings";
import { AppDispatch, StoreState } from "../../../redux/store";
import { clearLAEData, updateUserInfos, updateUserSettingsDefaultPageAndChat } from "../../../redux/users/usersSlice";
import { isDOBPlus18, nameFormatter } from "../../../utls/FunctionsUtil";
import EditCreatorAvatar from "./EditCreatorAvatar";
import "./index.less";

const CreatorEditProfilePage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { currentUser: user } = useFetchUserInfos(true);
  const { currentUserSettings } = useFetchUserSettings();
  const { loading, error, action } = useSelector((state: StoreState) => state.users);
  const [personalInfos, setPersonalInfos] = useState<IEditCreatorPersonalInfosRequest>(defaultCreatorEditPersonalInfosRequest);
  const [userSettingsInfos, setUserSettingsInfos] = useState<IEditCreatorChatDefaultPageRequest>(defaultCreatorEditChatDefaultPageInfosRequest);

  useEffect(() => {
    if (user?.infos.privateInfos) {
      setPersonalInfos({
        ...user.infos.privateInfos,
        city: user.infos.city,
        name: user.infos.name,
        description: user.infos.description,
      });
    }
    if (currentUserSettings) {
      setUserSettingsInfos({
        defaultPage: currentUserSettings.defaultPage,
        chat: currentUserSettings.chat,
      });
    }
  }, [user, currentUserSettings]);

  useEffect(() => {
    if (loading === REQUEST_LOADING_STATES.SUCCEEDED && action === "updateProfile") {
      message.success(t("updated-personal-information-successfully"));
      dispatch(clearLAEData());
    }

    if (loading === REQUEST_LOADING_STATES.FAILED && error && action === "updateProfile") {
      message.error(t(error));
      dispatch(clearLAEData());
    }
  }, [loading, error, action, t, dispatch]);

  const handleOnChange = (name: string, value: string) => {
    if (name === "dateOfBirth" && value && !isDOBPlus18(value)) {
      message.warning(t("minimum-age"));
      return;
    }
    if (name === "name" && value) {
      value = nameFormatter(value);
    }
    setPersonalInfos({ ...personalInfos, [name]: value });
  };

  const handleOnSettingsChange = (name: string, value: string) => {
    setUserSettingsInfos({ ...userSettingsInfos, [name]: value });
  };
  const registerClicked = async () => {
    if (userSettingsInfos.chat !== currentUserSettings?.chat || userSettingsInfos.defaultPage !== currentUserSettings?.defaultPage) {
      dispatch(updateUserSettingsDefaultPageAndChat(userSettingsInfos));
    }
    dispatch(updateUserInfos(personalInfos));
  };

  const handleIsBtnDisabled = () => {
    if (!personalInfos) return true;
    if (personalInfos.dateOfBirth !== user?.infos?.privateInfos?.dateOfBirth) return false;
    else if (personalInfos.name !== user?.infos?.name) return false;
    else if (personalInfos.description !== user?.infos?.description) return false;
    else if (personalInfos.city !== user?.infos?.city) return false;
    else return true;
  };

  return (
    <Layout className="gradient-background-black-blue">
      <Layout.Content className="content creatorEditProfileContent background-transparent">
        <Row className="relative justify-content-center header-title-container-30">
          <ArrowLeftIconComponent left={16} top={2} />
          <Typography.Text className="header-title-black-24">{t("edit-my-profile")}</Typography.Text>
        </Row>
        <EditCreatorAvatar user={user?.infos} t={t} />
        {user && (
          <div>
            <Row className="pl-20 pr-20 mt-20">
              <EditProfileSwitchComponent forChat={false} handleOnChange={handleOnSettingsChange} />
            </Row>
            <Row className="pl-20 pr-20 mt-20">
              <EditProfileSwitchComponent forChat={true} handleOnChange={handleOnSettingsChange} />
            </Row>
          </div>
        )}
        <Row className="w-full mt-24 pl-20 pr-20 mt-20">
          <Space className="w-full gap-13" direction="vertical">
            <Row gutter={7} align="middle">
              <Col span={12}>
                <Space className="w-full gap-6" direction="vertical">
                  <Typography.Text className="font-16-bold text-white-color">{t("identity")}</Typography.Text>
                  <InputComponent className="editInput" value={user?.infos?.firstName || ""} disabled />
                </Space>
              </Col>
              <Col span={12}>
                <Space className="w-full gap-6 mt-22" direction="vertical">
                  <InputComponent className="editInput" value={user?.infos?.lastName || ""} disabled />
                </Space>
              </Col>
            </Row>
            <Row>
              <Space className="w-full gap-6" direction="vertical">
                <Typography.Text className="font-16-bold text-white-color">{t("email-label")}</Typography.Text>
                <InputComponent className="editInput" type="text" value={user?.infos?.privateInfos?.email || ""} disabled />
              </Space>
            </Row>
            <Row>
              <Space className="w-full gap-6" direction="vertical">
                <Typography.Text className="font-16-bold text-white-color">{t("username")}</Typography.Text>
                <InputComponent className="editInput" type="text" value={user?.infos.username || ""} disabled />
              </Space>
            </Row>
            <Row>
              <Space className="w-full gap-6" direction="vertical">
                <Typography.Text className="font-16-bold text-white-color">{t("last-name-label")}</Typography.Text>
                <InputComponent
                  className="editInput"
                  type="text"
                  value={personalInfos?.name || ""}
                  onChange={(e) => handleOnChange("name", e.target.value)}
                />
              </Space>
            </Row>
            <Row>
              <Space className="w-full gap-6" direction="vertical">
                <Typography.Text className="font-16-bold text-white-color">{t("date-of-birth-label")}</Typography.Text>
                <DatePickerComponent
                  className="editDarkDatePicker"
                  date={personalInfos?.dateOfBirth || ""}
                  onChange={(value) => handleOnChange("dateOfBirth", value || "")}
                />
              </Space>
            </Row>
            <Row>
              <Space className="w-full gap-6" direction="vertical">
                <Typography.Text className="font-16-bold text-white-color">{t("position")}</Typography.Text>
                {/* <GooglePlacesAutocomplete
                  apiKey={process.env.REACT_APP_GOOGLE_API_KEY}
                  selectProps={{
                    placeholder: t("position"),
                    defaultInputValue: personalInfos?.city || "",
                    onChange: (location) => {
                      if (location) handleOnChange("city", location?.label);
                    },
                    styles: {
                      control: (provided) => ({
                        ...provided,
                        color: "#FFF",
                        backgroundColor: "rgba(77, 77, 77, 0.4)",
                        paddingLeft: 15,
                        border: "unset",
                        borderRadius: 15,
                        height: 42,
                      }),
                      input: (provided) => ({
                        ...provided,
                        color: "#FFF",
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: "#FFF",
                      }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        display: "none",
                      }),
                      indicatorSeparator: (provided) => ({
                        ...provided,
                        display: "none",
                      }),
                    },
                  }}
                /> */}
                <InputComponent
                  className="editInput"
                  type="text"
                  value={personalInfos?.city || ""}
                  onChange={(e) => handleOnChange("city", e.target.value)}
                />
                {/*<InputComponent className="darkInput" type="text" />*/}
              </Space>
            </Row>
            <Row>
              <Space className="w-full gap-6" direction="vertical">
                <Typography.Text className="font-16-bold text-white-color">{t("description")}</Typography.Text>
                <TextAreaComponent
                  className="bioEditTextArea"
                  value={personalInfos?.description || ""}
                  onChange={(e) => handleOnChange("description", e.target.value)}
                />
              </Space>
            </Row>
            {/* <Button className="saveBtn" onClick={registerClicked} disabled={!isModified}>
              {t("register")}
            </Button> */}
            {/* <Row>
              <Space className="w-full gap-6" direction="vertical">
                <Typography.Text className="font-13-bold text-white-color">{t("gender-label")}</Typography.Text>
                <Radio.Group className="w-full" value={user?.gender} buttonStyle="solid" onChange={(e) => handleOnChange("gender", e.target.value)}>
                  <Row gutter={8}>
                    {genders.map((gender, index) => {
                      return (
                        <Col className="mt-5" key={index} span={8}>
                          <Radio.Button className="mediaAmountRadioButton" value={gender.value}>
                            {gender.label}
                          </Radio.Button>
                        </Col>
                      );
                    })}
                  </Row>
                </Radio.Group>
              </Space>
            </Row> */}
          </Space>
        </Row>
        <StickyButtonComponent title={t("register")} handleClickButton={registerClicked} disabled={!handleIsBtnDisabled} />
      </Layout.Content>
    </Layout>
  );
};

export default CreatorEditProfilePage;
